import React, { useCallback, useRef, useState } from 'react';
import { Upload, X, FileVideo, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { mccUpload } from './mccApi';

/**
 * MccUploader — drag-and-drop file upload.
 *
 * Props:
 *   endpoint: '/marketing/uploads' or '/marketing/versions'
 *   extraFields: object to merge into the FormData (project_id, kind, etc.)
 *   accept: MIME accept string (e.g. 'video/*,image/*')
 *   multiple: allow multiple files
 *   dark: use cinematic dark theme (for raw footage / edit review areas)
 *   onUploaded: callback invoked with each successful response
 *   label: main heading text
 *   sub: secondary description
 */
export default function MccUploader({
  endpoint = '/marketing/uploads',
  extraFields = {},
  accept = '',
  multiple = true,
  dark = false,
  onUploaded,
  label = 'Drag & drop files here',
  sub = 'or click to browse — video, image, audio, docs. Max 2 GB per file.',
  compact = false,
  testid = 'mcc-uploader',
}) {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [queue, setQueue] = useState([]); // {id, name, progress, error}

  const start = useCallback(async (files) => {
    for (const file of Array.from(files || [])) {
      const jobId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      setQueue((q) => [...q, { id: jobId, name: file.name, size: file.size, progress: 0 }]);
      try {
        const fd = new FormData();
        fd.append('file', file);
        for (const [k, v] of Object.entries(extraFields || {})) {
          if (v !== undefined && v !== null && v !== '') fd.append(k, String(v));
        }
        // eslint-disable-next-line no-loop-func
        const res = await mccUpload(endpoint, fd, (p) => {
          setQueue((q) => q.map((j) => (j.id === jobId ? { ...j, progress: p } : j)));
        });
        setQueue((q) => q.map((j) => (j.id === jobId ? { ...j, progress: 100, done: true } : j)));
        if (onUploaded) onUploaded(res, file);
        toast.success(`Uploaded ${file.name}`);
      } catch (e) {
        setQueue((q) => q.map((j) => (j.id === jobId ? { ...j, error: e.message } : j)));
        toast.error(`Failed: ${file.name}${e.message ? ` — ${e.message}` : ''}`);
      }
    }
    // Auto-purge finished jobs after a short delay
    setTimeout(() => setQueue((q) => q.filter((j) => !j.done)), 2500);
  }, [endpoint, extraFields, onUploaded]);

  const bg = dark
    ? (dragOver ? 'border-indigo-400 bg-indigo-500/10' : 'border-slate-700 bg-black/40')
    : (dragOver ? 'border-indigo-400 bg-indigo-50' : 'border-slate-300 bg-slate-50/60');
  const textCls = dark ? 'text-slate-200' : 'text-slate-800';
  const subCls = dark ? 'text-slate-400' : 'text-slate-500';

  return (
    <div>
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault(); setDragOver(false);
          if (e.dataTransfer?.files?.length) start(e.dataTransfer.files);
        }}
        onClick={() => inputRef.current?.click()}
        className={`border-2 border-dashed rounded-xl ${compact ? 'p-4' : 'p-8'} text-center cursor-pointer transition ${bg}`}
        data-testid={testid}
      >
        <input
          ref={inputRef}
          type="file"
          className="hidden"
          multiple={multiple}
          accept={accept}
          onChange={(e) => e.target.files && start(e.target.files)}
        />
        <Upload className={`w-8 h-8 mx-auto mb-2 ${textCls}`} />
        <p className={`text-sm font-semibold ${textCls}`}>{label}</p>
        <p className={`text-xs ${subCls} mt-1`}>{sub}</p>
      </div>

      {queue.length > 0 && (
        <div className={`mt-3 space-y-2 ${dark ? 'text-slate-200' : 'text-slate-800'}`}>
          {queue.map((j) => (
            <div key={j.id} className={`border rounded-lg px-3 py-2 ${dark ? 'border-slate-700 bg-slate-800/50' : 'border-slate-200 bg-white'}`}>
              <div className="flex items-center gap-2 text-xs">
                <FileVideo className="w-4 h-4 text-indigo-500 flex-shrink-0" />
                <span className="font-medium truncate flex-1">{j.name}</span>
                <span className="text-slate-500 flex-shrink-0 tabular-nums">
                  {j.error ? 'Failed' : j.done ? 'Done' : `${j.progress}%`}
                </span>
                {j.error ? (
                  <X className="w-4 h-4 text-rose-500" />
                ) : !j.done ? (
                  <Loader2 className="w-4 h-4 text-indigo-500 animate-spin" />
                ) : null}
              </div>
              <div className={`h-1 rounded-full mt-1.5 overflow-hidden ${dark ? 'bg-slate-700' : 'bg-slate-200'}`}>
                <div
                  className={`h-full ${j.error ? 'bg-rose-500' : 'bg-indigo-500'} transition-all`}
                  style={{ width: `${j.progress}%` }}
                />
              </div>
              {j.error ? <p className="text-[11px] text-rose-500 mt-1">{j.error}</p> : null}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
